import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '../contexts/AlertsContext';
import { useToast } from '../contexts/ToastContext';
import helperService from '../services/helperService';
import propertiesService from '../services/propertiesService';
import alertsService from '../services/alertsService';
import './CreateAlert.css';

const ALERT_TYPES = [
  { id: 'Range Fire',        icon: 'local_fire_department', color: '#e63946' },
  { id: 'Flash Flood',       icon: 'water',                 color: '#2980b9' },
  { id: 'Severe Weather',    icon: 'cloud',                 color: '#7b8c9d' },
  { id: 'Livestock Emergency', icon: 'pets',                  color: '#e67e22' },
  { id: 'Equipment Failure',   icon: 'construction',          color: '#27ae60' },
  { id: 'Medical Emergency', icon: 'medical_services',      color: '#c0392b' },
  { id: 'Other',             icon: 'help_outline',          color: '#8e44ad' },
];

function CreateAlert() {
  const { addAlert } = useAlerts();
  const { addToast } = useToast();
  const navigate = useNavigate();

  /* ── Wizard phase ───────────────────────── */
  const [phase, setPhase] = useState('quick'); // 'quick' | 'details' | 'sent'
  const [createdAlertId, setCreatedAlertId] = useState(null);

  /* ── Form data ──────────────────────────── */
  const [properties, setProperties] = useState([]);
  const [alertData, setAlertData] = useState({
    type: '', location: '', severity: '', description: '', coordinates: null,
  });

  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  const [skillsCatalog, setSkillsCatalog]       = useState([]);
  const [catalogLoading, setCatalogLoading]     = useState(true);
  const [neededEquipment, setNeededEquipment]   = useState([]);
  const [neededSkills, setNeededSkills]         = useState([]);
  const [isSaving, setIsSaving]                 = useState(false);

  /* ── Fetch catalogs + properties on mount ─ */
  useEffect(() => {
    Promise.all([
      helperService.getEquipmentCatalog(),
      helperService.getSkillsCatalog(),
      propertiesService.getProperties(),
    ]).then(([eqCat, skCat, props]) => {
      setEquipmentCatalog(eqCat);
      setSkillsCatalog(skCat);
      const list = Array.isArray(props) ? props : [];
      setProperties(list);
      if (list.length === 1) {
        setAlertData(prev => ({ ...prev, location: String(list[0].id) }));
      }
    }).finally(() => setCatalogLoading(false));
  }, []);

  const toggleId = (setList, id) =>
    setList(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const selectType     = (type)     => setAlertData(prev => ({ ...prev, type }));
  const selectSeverity = (severity) => setAlertData(prev => ({ ...prev, severity }));

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setAlertData(prev => ({ ...prev, location: value }));
  };

  const handleCustomLocationChange = (e) => {
    setAlertData(prev => ({ ...prev, location: e.target.value }));
  };

  const resolvePayload = () => {
    const selectedProperty = alertData.location && alertData.location !== 'custom'
      ? properties.find(p => p.id === Number(alertData.location))
      : null;

    let coordinates = null;
    if (selectedProperty?.boundary?.length) {
      const pts = selectedProperty.boundary;
      coordinates = {
        lat: pts.reduce((sum, p) => sum + p.lat, 0) / pts.length,
        lng: pts.reduce((sum, p) => sum + p.lng, 0) / pts.length,
      };
    }

    return {
      ...alertData,
      location:    selectedProperty?.name ?? alertData.location,
      property_id: selectedProperty?.id   ?? null,
      coordinates,
      neededEquipment,
      neededSkills,
    };
  };

  /* ── Phase 1: send the quick alert ─────── */
  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!alertData.type || !alertData.severity || !alertData.location) return;

    setIsSaving(true);
    const payload = resolvePayload();
    try {
      const result = await alertsService.createAlert(payload);
      addAlert({ ...payload, id: result.id });
      setCreatedAlertId(result.id);
      setPhase('details');
      addToast('Alert sent! Responders are being notified.', 'success');
    } catch (err) {
      addToast(err.message || 'Could not submit alert. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Phase 2: add details ──────────────── */
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!createdAlertId) return;

    setIsSaving(true);
    try {
      await alertsService.updateAlert(createdAlertId, {
        description:      alertData.description,
        neededEquipment,
        neededSkills,
      });
      addToast('Details updated!', 'success');
      setPhase('sent');
    } catch (err) {
      addToast(err.message || 'Could not update details.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Render: Phase 1 ───────────────────── */
  const renderPhaseQuick = () => (
    <form onSubmit={handleQuickSubmit} className="alert-form">
      <h2 className="phase-title">Quick Alert</h2>
      <p className="phase-subtitle">Tap the emergency type to send immediately.</p>

      {/* Type tiles */}
      <div className="type-tiles">
        {ALERT_TYPES.map(t => (
          <button
            key={t.id}
            type="button"
            className={`type-tile${alertData.type === t.id ? ' selected' : ''}`}
            onClick={() => selectType(t.id)}
            aria-label={t.id}
          >
            <span className="material-icons type-icon" style={{ color: t.color }}>{t.icon}</span>
            <span className="type-label">{t.id}</span>
          </button>
        ))}
      </div>

      {/* Location */}
      {properties.length > 1 && (
        <div className="form-group">
          <label htmlFor="location">Location</label>
          <select
            id="location"
            value={alertData.location}
            onChange={handleLocationChange}
            required
          >
            <option value="">Select property</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
            <option value="custom">Other (custom)</option>
          </select>
        </div>
      )}
      {properties.length === 1 && (
        <div className="preselected-info">
          <span className="material-icons">location_on</span>
          <span>{properties[0].name}</span>
        </div>
      )}

      {alertData.location === 'custom' && (
        <div className="form-group">
          <label htmlFor="customLocation">Custom Location</label>
          <input
            type="text"
            id="customLocation"
            placeholder="e.g., Sector 7, North Pasture"
            onChange={handleCustomLocationChange}
            required
          />
        </div>
      )}

      {/* Severity */}
      <div className="form-group">
        <label>Severity</label>
        <div className="severity-buttons">
          <button
            type="button"
            className={`severity-btn low${alertData.severity === 'Low' ? ' selected' : ''}`}
            onClick={() => selectSeverity('Low')}
          >
            Low
          </button>
          <button
            type="button"
            className={`severity-btn medium${alertData.severity === 'Medium' ? ' selected' : ''}`}
            onClick={() => selectSeverity('Medium')}
          >
            Medium
          </button>
          <button
            type="button"
            className={`severity-btn high${alertData.severity === 'High' ? ' selected' : ''}`}
            onClick={() => selectSeverity('High')}
          >
            High
            <span className="severity-hint">Call 911 first</span>
          </button>
        </div>
      </div>

      <div className="form-buttons">
        <button
          type="submit"
          className="submit-button"
          disabled={isSaving || !alertData.type || !alertData.severity || !alertData.location}
        >
          {isSaving ? 'Sending…' : 'Send Alert Now'}
        </button>
        <button type="button" className="cancel-button" onClick={() => navigate('/dashboard')}>
          Cancel
        </button>
      </div>
    </form>
  );

  /* ── Render: Phase 2 ────────────────────── */
  const renderPhaseDetails = () => (
    <div className="alert-form">
      <div className="success-banner">
        <span className="material-icons">check_circle</span>
        <div>
          <strong>Alert sent!</strong>
          <span>Neighbors are being notified. Add details while help is coming.</span>
        </div>
      </div>

      <form onSubmit={handleDetailsSubmit}>
        <h2 className="phase-title">Help Responders Prepare</h2>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={alertData.description}
            onChange={(e) => setAlertData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="What's happening? Any details responders should know..."
            rows="4"
          />
        </div>

        <div className="cap-section">
          <h3>Equipment Needed</h3>
          <p className="section-hint">Select the equipment that would help responders.</p>
          <div className="chip-grid">
            {catalogLoading
              ? <p className="section-hint">Loading…</p>
              : equipmentCatalog.map(item => (
                <button
                  key={item.id} type="button"
                  className={`chip${neededEquipment.includes(item.id) ? ' selected' : ''}`}
                  onClick={() => toggleId(setNeededEquipment, item.id)}
                >
                  {item.label}
                </button>
              ))
            }
          </div>
        </div>

        <div className="cap-section">
          <h3>Skills &amp; Certifications Needed</h3>
          <p className="section-hint">Select relevant skills or certifications for responders.</p>
          <div className="chip-grid">
            {catalogLoading
              ? <p className="section-hint">Loading…</p>
              : skillsCatalog.map(item => (
                <button
                  key={item.id} type="button"
                  className={`chip${neededSkills.includes(item.id) ? ' selected' : ''}`}
                  onClick={() => toggleId(setNeededSkills, item.id)}
                >
                  {item.label}
                </button>
              ))
            }
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" className="submit-button" disabled={isSaving}>
            {isSaving ? 'Updating…' : 'Add Details'}
          </button>
          <button type="button" className="cancel-button" onClick={() => navigate('/alerts')}>
            Skip — Go to Alerts
          </button>
        </div>
      </form>
    </div>
  );

  /* ── Render: Phase 3 ─────────────────────── */
  const renderPhaseSent = () => (
    <div className="alert-form sent-view">
      <div className="success-banner">
        <span className="material-icons">check_circle</span>
        <div>
          <strong>All set!</strong>
          <span>Your alert has been created and updated. Responders are on the way.</span>
        </div>
      </div>
      <div className="form-buttons">
        <button className="submit-button" onClick={() => navigate('/alerts')}>
          Go to Alerts
        </button>
        <button className="cancel-button" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <div className="create-alert-page">
      <div className="container">
        <h1>Create Emergency Alert</h1>
        {phase === 'quick'    && renderPhaseQuick()}
        {phase === 'details'  && renderPhaseDetails()}
        {phase === 'sent'     && renderPhaseSent()}
      </div>
    </div>
  );
}

export default CreateAlert;
